/* eslint-disable no-unused-vars */

import { useCallback, useEffect, useState } from "react";
import { LoaderControl } from "../../../../components/LoaderControl";
import {
  getSummaryRangeDates,
  MDY_DATE_FORMAT_THREE,
} from "../../../../utils/filters";
import { Titlesubtitle } from "../../../../components/Common";
import { InputField } from "../../../../components/InputField";
import SearchIcon from "../../../../assets/icons/search-icon.png";
import ss from "../../../../components/InputField/input.module.scss";
import "./reports.scss";
import { ReportTable } from "./components";
import { PaginationState } from "@tanstack/react-table";
import { SummaryRanges } from "../../../../sharedTypes/wallet-types";
import { addLinkDownload, debounce } from "../../../../utils/index";
import { useToast } from "../../../../components/Toast/toast-context";
import { useGetHistory } from "../../../../hooks";
import { ReportDataModel } from "../../../../sharedTypes/report-types";
import { HistoryHeader } from "../../../../components/History";
import { GenericFilter } from "../../../../components/Filters";
import { useLazyGetDownloadHistoryQuery } from "../../../../requests/history";
import { reportStatusOptions } from "../../../../utils/data";
import { format, startOfYear, endOfYear, setDate } from "date-fns";

export function Reports() {
  const [openFilterDialog, setOpenFilterDialog] = useState("");
  // setDateFilter
  const [dateFilter] = useState<SummaryRanges>("THIS_YEAR");

  const [filter, setFilter] = useState<{
    from: string | Date;
    to: string | Date;
    status: string;
    search: string;
  }>({
    from: "",
    to: "",
    status: "",
    search: "",
  });

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: 10,
  });

  const { startDate, endDate } = getSummaryRangeDates(dateFilter);

  const { getHistory, getCount, refetch, isLoading, isFetching, isError } =
    useGetHistory({
      name: "reports",
      startDate: filter.from
        ? (filter.from as string)
        : format(startDate as any, MDY_DATE_FORMAT_THREE),
      endDate: filter.to
        ? (filter.to as string)
        : format(endDate as any, MDY_DATE_FORMAT_THREE),
      search: filter.search,
      status: filter.status,
      page: pagination.pageIndex as unknown as string,
    });

  const toast = useToast();

  const debouncedSearch = useCallback(
    debounce((newSearch: string) => {
      setFilter((prevFilter) => ({ ...prevFilter, search: newSearch }));
    }, 1000),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleRefetch = (
    to?: string | Date,
    from?: string | Date,
    status?: string
  ) => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      to: to || "",
      from: from || "",
      status: status || "",
    }));
    closeFilterDialog();
  };

  const closeFilterDialog = () => setOpenFilterDialog("");

  const [fetchCSV, { isFetching: csvFetching, isLoading: csvLoading }] =
    useLazyGetDownloadHistoryQuery();

  let downloadCSV = async (
    to?: Date | string,
    from?: Date | string,
    status?: string
  ) => {
   const res = await fetchCSV({
      name: "reports",
      fileType: "CSV",
      startDate:
        from || format(setDate(startOfYear(new Date()), 5), "yyyy-MM-dd"),
      endDate: to || format(endOfYear(new Date()), "yyyy-MM-dd"),
      responseStatus: status,
      transactionDirection: status,
    });

    if (res && res.isError) {
      toast?.open({
        variant: "warning",
        message: "An error occured generating report",
      });
    } else {
      addLinkDownload(
        `${from?.toLocaleString()}_${to?.toLocaleString()}_reports`,
        res?.data?.data?.data as unknown as string
      );

      toast?.open({
        variant: "success",
        message: "Transactions was successfully downloaded",
      });
      closeFilterDialog();
    }
  };

  useEffect(() => {
    debouncedSearch(search);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, debouncedSearch]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [pagination]);

  useEffect(() => {
    refetch();
  }, [dateFilter]);

  return (
    <main className="reports-container tw-mx-auto md:tw-max-w-[80vw] lg:tw-max-w-[85vw] xl:tw-max-w-[90vw] 2xl:max-w-[100em] ">
      <div className="reports-container--wrapper tw-flex tw-flex-col tw-gap-[1.5em]">
        <HistoryHeader
          title="API Calls"
          openFilter={() => setOpenFilterDialog("filter")}
          downloadFn={() => setOpenFilterDialog("download")}
        />

        <div className="tw-w-full tw-flex tw-items-center tw-justify-between">
          <Titlesubtitle
            title="API calls details"
            tclass="!tw-text-[1em] tw-text-neutral-900"
            subtitle="These are all the API calls that have occurred on the API Market"
            sclass="!tw-text-[.87em] tw-w-[80%]"
          />
          <div className="md:tw-w-[40%] 2xl:tw-w-[20%]">
            <InputField
              name="search"
              placeholder="Search "
              className="tw-w-full tw-h-[40px] !tw-bg-neutral-100 !tw-border-[5px] !tw-border-neutral-300"
              iconRight={
                <img
                  src={SearchIcon}
                  className={`${ss["iconriht"]} !tw-w-[20px] !tw-h-[20px] tw-absolute !tw-top-[25%] !tw-bottom-0 !tw-right-[5%]`}
                  alt="search"
                />
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setSearch(e.target.value);
              }}
            />
          </div>
        </div>

        <section className="tw-mt-[1.5em]">
          <LoaderControl
            loading={isFetching}
            overlay={isLoading || isFetching}
            error={isError}
            errorControlOnClick={refetch}
            errorTitle="An error occurred. Please reload the widget."
          >
            {
              <ReportTable
                data={getHistory as ReportDataModel[]}
                total={getCount as number}
                pagination={pagination}
                setPagination={setPagination}
              />
            }
          </LoaderControl>
        </section>
      </div>

      {/* for filter */}
      <GenericFilter
        title="Choose a period to filter API calls "
        btnText="Filter"
        open={openFilterDialog === "filter"}
        closeHandler={() => setOpenFilterDialog("")}
        filterFn={handleRefetch}
        loading={isLoading || isFetching}
        statusData={reportStatusOptions}
        statusText="Choose a status to filter API calls"
        downloadFn={downloadCSV}
      />

      {/* for download */}
      <GenericFilter
        title="Choose a period to download API calls"
        btnText="Filter"
        open={openFilterDialog === "download"}
        closeHandler={closeFilterDialog}
        filterFn={downloadCSV}
        statusText="Choose a status to filter API calls"
        statusData={reportStatusOptions}
        loading={csvFetching || csvLoading}
      />
    </main>
  );
}
